import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthService } from '../../services/auth.service';
import {
  catchError,
  combineLatest,
  concat,
  filter,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { AuthActions } from '../state/auth/auth.actions';
import { UserActions } from '../state/user/user.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { UserSettingsActions } from '../state/user-settings/user-settings.actions';
import { LocalStorageKeys } from '../../consts/shared.const';
import { SessionStorageService, errMessage } from '@ui';
import { UserSettingsModels } from '@share-models';
import { OrganizationActions } from '../state/organization/organization.actions';
import { PermissionsActions } from '../state/permissions/premissions.actions';
import { userFeature } from '../state/user/user.feature';
import { userSettingsFeature } from '../state/user-settings/user-settings.feature';
import { organizationFeature } from '../state/organization/organization.feature';
import { permissionsFeature } from '../state/permissions/permissions.feature';

export const startLoadRequiredData = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    sessionStorageService = inject(SessionStorageService),
  ) => {
    return actions$.pipe(
      ofType(AuthActions.startLoadRequiredData),
      switchMap(() => {
        const interfaceLanguage = sessionStorageService.getItem(
          LocalStorageKeys.LANGUAGE,
        );
        if (interfaceLanguage) {
          sessionStorageService.removeItem(LocalStorageKeys.LANGUAGE);
        }

        const fanOutActions = [
          UserActions.getUserProfile(),
          UserSettingsActions.getMe({
            language: interfaceLanguage ?? UserSettingsModels.LANGUAGE.ENGLISH,
          }),
          OrganizationActions.getActiveOrganization(),
          OrganizationActions.getMyOrganizations(),
          PermissionsActions.getUserPermissions(),
        ];

        // App bootstrap (see initApp in app.config.ts) waits on
        // requiredDataLoadedSuccess before route guards can run, so it must
        // only fire once every permission-relevant slice — profile, user
        // settings, active organization, permissions — has settled
        // (success or fail), not just as soon as the fastest one resolves.
        //
        // This relies on each feature's `loaded` flag starting `false` here,
        // which holds today because startLoadRequiredData only ever fires
        // once per page load (once from initApp's bootstrap, once from the
        // one-time OAuth code exchange) and every logout path hard-navigates
        // (see AuthService.logout), tearing down the whole store. If a
        // future change ever re-dispatches startLoadRequiredData within the
        // same session without a full reload, these flags must be reset to
        // false first, or a stale `true` from the prior load will let this
        // fire immediately and reintroduce the guard/navigation race this
        // code exists to prevent.
        const requiredDataLoaded$ = combineLatest([
          store.select(userFeature.selectLoaded),
          store.select(userSettingsFeature.selectLoaded),
          store.select(organizationFeature.selectLoaded),
          store.select(permissionsFeature.selectLoaded),
        ]).pipe(
          filter(
            ([
              userLoaded,
              userSettingsLoaded,
              organizationLoaded,
              permissionsLoaded,
            ]) =>
              userLoaded &&
              userSettingsLoaded &&
              organizationLoaded &&
              permissionsLoaded,
          ),
          take(1),
          map(() => AuthActions.requiredDataLoadedSuccess()),
        );

        return concat(of(...fanOutActions), requiredDataLoaded$);
      }),
    );
  },
  { functional: true },
);

export const socialLoginByCode = createEffect(
  (actions$ = inject(Actions), api = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthActions.socialLoginByCode),
      switchMap(({ code }) =>
        api.socialLoginByCode(code).pipe(
          map((authResponse) =>
            UserActions.setStoreSession({
              data: {
                idToken: authResponse.id_token,
                accessToken: authResponse.access_token,
                expiredAt: authResponse.expires_in,
              },
            }),
          ),
          catchError((err: HttpErrorResponse) => {
            const msg = errMessage(err);
            return of(AuthActions.logout({ msg }));
          }),
        ),
      ),
    );
  },
  { functional: true },
);
